<template>
    <div>
        <main
            :class="{ 'api-template': fallback }"
            :data-legacy-product-sku="story?.content?.product_id"
            :data-propel-product-sku="story?.content?.propelProductSKU"
        >
            <component :is="story?.content?.component" v-if="story" :blok="story?.content" />
        </main>
    </div>
</template>

<script setup lang="ts">
import type { IL4ApiProductFull } from '~/../types/components/L4Products';
import removeAccents from 'remove-accents';
import { replaceRootHeaderAndFooter } from '~/utils/sbHelper';
import { paths, getLanguageFromRoute, getProductTypeSlug } from '~/utils/getLanguage';
const { locale, locales, t } = useI18n();
const route: any = useRoute();
const { feefoMerchantId } = useFeefo();

// Constant variable for access to Runtime Config
const { STORYBLOK_FOLDER, STORYBLOK_VERSION, STORYBLOK_ROUTE_BLOCKED }: any = useRuntimeConfig().public;

const fallback = ref(false);

// If non-supported locale, throw 404
if (!route.matched[0].path.includes(locale.value)) {
    throw createError({
        statusCode: 404,
        statusMessage: 'Page Not Found',
        fatal: true,
    });
}

const { data: feefoData }: any = await useAsyncData(
    'feefo_' + feefoMerchantId + '_' + route.params.product_id,
    async () => {
        return await $fetch(
            `https://api.feefo.com/api/20/reviews/summary/product?merchant_identifier=${feefoMerchantId}&product_sku=${route.params.product_id}&since_period=All&unanswered_feedback=include`
        );
    }
);

if (feefoData.value) {
    const feefoSchema = `{
    "@context": "https://schema.org/",
    "@type": "Product",
    "name": "${t('reviewSchema.name')}",
    "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": ${feefoData.value?.rating?.rating},
        "bestRating": ${feefoData.value?.rating?.max},
        "reviewCount": ${feefoData.value?.meta?.count}
    }
}`;

    useHead({
        script: [{ type: 'application/ld+json', innerHTML: feefoSchema }],
    });
}

// Need to resolve shared components to allow the storyblok API
// to expose this for us to use.
const resolveRelations: string[] = [
    'CoreSharedComponent.SharedComponent',
    'L4ProductPage.airport',
    'L4FooterContainer.footer',
    'L4HeaderContainer.header',
    'L4QuoteFormExtended.airport',
    'L4QuoteFormHotels.airport',
    'L4QuoteFormExtended.keysellingpoints',
    'L4BlockUSP.block',
    'L4CustomerReviews.reviewsBlock',
    'L4QuoteForm.airport',
    'L4Testimonial.review_what',
    'L4ProductHeader.product',
    'L4ProductHeader.airport',
    'L4ProductGrid.products',
    'L4Map.products',
    'L4Map.airport',
    'L4ProductGrid.products',
    'L4TrustedPartners.partners',
    'L4ProductGrid.products',
    'L4ProductMap.products',
    'L4ProductMap.airport',
    'L4AirportParkingTabs.airport',
    'L4ProductPricingTable.carParks',
    'L4CarParkingOptions.products',
    'L4ProductPricingTable.carParks',
    'L4CarParkingOptions.airport',
    'L4TopRatedCarParks.carParks',
    'L4TransferOptionsContainer.options_component',
    'L4EmailSignupNew.signup',
];

const { data: apiProduct } = await useAsyncData<IL4ApiProductFull>(
    'product',
    () =>
        $privateApi<IL4ApiProductFull>(`parking/products/public/${route.params.product_id}`, {
            method: 'GET',
            params: {
                lang: getLanguageFromRoute(route, locale.value, locales.value),
            },
        }).catch((error) => {
            console.error('Error fetching product:', error);
            return null;
        }),
    { lazy: true }
);

provide('apiProduct', apiProduct);
provide('airportFromApiProduct', apiProduct?.value?.locationCode || '');

if (!apiProduct.value) {
    navigateTo(route.path.replace(`${route.params.product}/${route.params.product_id}`, ''), {
        external: true,
        redirectCode: 301,
    });
}

const routePath = route.path.includes(`${route.params.product}/${route.params.product_id}`)
    ? route.path.replace(`${route.params.product}/${route.params.product_id}`, `${route.params.product_id}`)
    : route.path;

const sbLocation = `${STORYBLOK_FOLDER}/pages${routePath}`;

const productTemplateName = locale.value === 'uk' ? 'product-template-with-new-searchbox' : 'product-template';

const isInVisualEditor = !!route?.query?.['_storyblok_tk[token]']; //Check if frontend is opened in visual editor
// Perform request to Storyblok to retrieve content
const data: any = await useAsyncStoryblok(sbLocation, {
    // Enable deep reactivity to fix live editing when in visual editor.
    // Because of changes for useAsyncData in Nuxt4 https://github.com/storyblok/monoblok/issues/210#issuecomment-3172675601
    deep: isInVisualEditor,
    api: {
        version: STORYBLOK_VERSION === 'published' ? 'published' : 'draft',
        language: locale.value,
        resolve_relations: resolveRelations,
    },
    bridge: {
        resolveRelations: resolveRelations,
    },
});

const story = ref(data?.story.value);
const error = ref(data?.error.value);

if (error.value) {
    // Perform request to Storyblok to retrieve content
    const templateData = await useAsyncStoryblok(`${STORYBLOK_FOLDER}/pages/${productTemplateName}`, {
        // Enable deep reactivity to fix live editing when in visual editor.
        // Because of changes for useAsyncData in Nuxt4 https://github.com/storyblok/monoblok/issues/210#issuecomment-3172675601
        deep: isInVisualEditor,
        api: {
            version: STORYBLOK_VERSION === 'published' ? 'published' : 'draft',
            language: locale.value,
            resolve_relations: resolveRelations,
        },
        bridge: {
            resolveRelations: resolveRelations,
        },
    });

    story.value = templateData?.story.value;
    error.value = templateData?.error.value;
    fallback.value = true;
}

if (story) {
    const decodedPath = decodeURI(route.path);
    const productLocationPath = decodedPath
        .replace(`/${route.params.product}/${route.params.product_id}`, '')
        .replace(' ', '-');
    const sbProductLocationPath = `/${STORYBLOK_FOLDER}/pages${productLocationPath}`;

    const headerData = await useAsyncStoryblok(sbProductLocationPath, {
        api: {
            version:
                STORYBLOK_VERSION === 'published'
                    ? ('published' as 'published' | 'draft')
                    : ('draft' as 'published' | 'draft'),
            language: getLanguageFromRoute(route, locale.value, locales.value),
            resolve_relations: resolveRelations,
        },
        bridge: {
            resolveRelations: resolveRelations,
        },
    });

    story.value = replaceRootHeaderAndFooter(headerData?.story.value, story.value);
}

if (story) {
    let config: { STORYBLOK_VERSION: string; STORYBLOK_FOLDER: string } = {
        STORYBLOK_VERSION: STORYBLOK_VERSION,
        STORYBLOK_FOLDER: STORYBLOK_FOLDER,
    };
    setSeoMeta(story.value?.content?.seo, config, route);
    successFetchStory();
}

useHead({
    title: apiProduct?.value?.displayName ? apiProduct?.value?.displayName : story?.value?.name,
});

function successFetchStory() {
    const pageName = convertTitleToSlug(story.value.name);
    const name = convertTitleToSlug(apiProduct?.value?.displayName ?? story.value.name);
    const lang = getLanguageFromRoute(route, locale.value, locales.value);
    const realTypeSlugFromRoute = route.params.type;
    const typeSlug = getTypeSlug(realTypeSlugFromRoute);

    let redirectTo = null;
    if (
        (name !== route.params.product && pageName !== productTemplateName) ||
        (apiProduct.value && name !== route.params.product)
    ) {
        redirectTo = route.path.replace(route.params.product, name);
    }

    if (typeSlug) {
        const translatedTypeSlug = getProductTypeSlug(lang, getTypeSlug(realTypeSlugFromRoute));
        if (translatedTypeSlug && realTypeSlugFromRoute !== translatedTypeSlug) {
            redirectTo = (redirectTo || route.path).replace(`/${realTypeSlugFromRoute}/`, `/${translatedTypeSlug}/`);
        }
    }

    if (redirectTo) {
        console.error('Redirect to right slug:', route.path.replace(route.params.product, name));
        return navigateTo(redirectTo, {
            external: true,
            redirectCode: 301,
        });
    }

    if (pageName === productTemplateName && !apiProduct.value) {
        throw createError({
            statusCode: 404,
            statusMessage: 'Page Not Found',
            fatal: true,
        });
    }
}

function getStoryblokBlockedRoutes() {
    if (STORYBLOK_ROUTE_BLOCKED) {
        const blocked = STORYBLOK_ROUTE_BLOCKED.split(', ');
        return blocked.some((block) => route.path.includes(block));
    } else {
        return false;
    }
}

// Send user to error page if no story found
if (getStoryblokBlockedRoutes()) {
    throw createError({
        statusCode: 404,
        statusMessage: 'Page Not Found',
        fatal: true,
    });
}

if (error?.value) {
    throw createError({
        statusCode: error.value.statusCode ?? 500,
        statusMessage: error.value.statusMessage ?? 'Failed to load page',
        fatal: true,
    });
}

onMounted(() => {
    // Allow use of Storyblok Bridge (Visual Editor)
    useStoryblokBridge(story?.value?.id, (evStory: any) => (story.value = evStory), {
        resolveRelations,
    });
});

provide('propelProductSKU', story?.value?.content?.propelProductSKU || '');
provide('templatePage', fallback.value);

function getTypeSlug(routeSlug: string): string {
    for (const group of Object.values(paths) as Array<Record<string, string>>) {
        if (Object.values(group).includes(routeSlug)) {
            return group.en || routeSlug;
        }
    }
    return null;
}

function convertTitleToSlug(title: string): string {
    const noAccents = removeAccents(title);
    return noAccents
        .toLowerCase()
        .replaceAll(/[^a-zA-Z0-9\-\s/]+/g, '')
        .replaceAll(' ', '-')
        .replaceAll('/', '-')
        .replaceAll(/[\s-]+\s*/g, '-')
        .replaceAll(/-+/g, '-')
        .replaceAll(/[\s\-/]+$/g, '')
        .replaceAll(/^[\s-]+/g, '');
}
</script>
